"use client";
import { Control, UseFormClearErrors } from "react-hook-form";
import MultiSelectInput from "../../../components/MultiSelectInput";
import NewSelectInput from "../../../components/NewSelectInput";
import { useWarehouseListData } from "../../../apis/warehouseApi";
import { useAllCompanyFetchData } from "../../../apis/fleetApis";
import NewinputField from "../../../components/NewInput";
import { IconUser } from "@tabler/icons-react";
import { T } from "../../../context/LanguageContext";

type AssignModalProps = {
  register?: any;
  errors?: any;
  setValue: (name: string, value: any) => void;
  watch: (name: string) => any;
  clearErrors?: UseFormClearErrors<any>;
  control: Control<any>;
  setMode?: (mode: string) => void;
  singleMode?: boolean;
};

const SectionHeader = ({ icon: Icon, title }: { icon: any; title: string }) => (
  <div className="flex items-center gap-2 border-b border-[#FBD037]/30 pb-2 mb-3">
    <div className="p-1.5 rounded-lg bg-[#FBD037]/20">
      <Icon size={16} className="text-[#212121]" stroke={1.5} />
    </div>
    <p className="text-[#212121]/80 uppercase text-[11px] font-bold tracking-wide">
      <T>{title}</T>
    </p>
  </div>
);

const RequestSendModal: React.FC<AssignModalProps> = ({
  register,
  errors,
  setValue,
  watch,
  clearErrors,
  singleMode,
}) => {
  const { data: warehouses, data: warehouseDes } = useWarehouseListData();
  const originWarehouseId = watch("originWarehouseId");
  const destinationWarehouseId = watch("destinationWarehouseId");

  const { data: companyData } = useAllCompanyFetchData();
  const linehaulCompanyId = watch("linehaulCompanyId");

  return (
    <div className="w-full space-y-4">
      <div className="space-y-4 p-4">
        <div className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3 relative">
          <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
          <SectionHeader icon={IconUser} title="Personal Info" />
          <div className="grid grid-cols-3 gap-4">
            <NewinputField
              title="Company Emails"
              type="email"
              placeholder="Enter email"
              name="fromEmail"
              register={register}
              error={errors.fromEmail}
              validation={{
                required: "Company Emails is required",
                setValueAs: (value: string) => value?.toLowerCase(),
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: "Please enter a valid email address",
                },
              }}
            />
            <MultiSelectInput
              className="space-y-1"
              labelClass=""
              label="Sent to"
              name="linehaulCompanyId"
              options={
                companyData?.map((d: any) => ({
                  id: d.id,
                  value: d.companyName,
                  label: d.companyName,
                })) || []
              }
              value={linehaulCompanyId}
              onChange={(selected: any) => {
                setValue("linehaulCompanyId", selected);
              }}
              error={errors.linehaulCompanyId}
              disabled={singleMode}
            />

            <NewSelectInput
              name="originWarehouseId"
              label="Origin warehouse"
              selectOptions={
                warehouses?.warehouses
                  ?.filter?.(
                    (warehouse: any) => warehouse?.id !== destinationWarehouseId,
                  )
                  ?.map((w: any) => ({
                    id: w?.id,
                    value: w?.id,
                    text: w?.name,
                  })) || []
              }
              clearErrors={clearErrors}
              register={register}
              validation={{ required: "Warehouse is required" }}
              error={errors.originWarehouseId}
              disabled={!warehouses || warehouses?.warehouses?.length === 0}
              value={originWarehouseId}
              setValue={(val: string) => {
                setValue("originWarehouseId", val);
              }}
            />
            <NewSelectInput
              name="destinationWarehouseId"
              label="Destination warehouse"
              selectOptions={
                warehouseDes?.warehouses
                  ?.filter?.(
                    (warehouse: any) => warehouse?.id !== originWarehouseId,
                  )
                  ?.map((w: any) => ({
                    id: w?.id,
                    value: w?.id,
                    text: w?.name,
                  })) || []
              }
              register={register}
              clearErrors={clearErrors}
              validation={{ required: "Warehouse is required" }}
              error={errors.destinationWarehouseId}
              disabled={!warehouseDes || warehouseDes?.warehouses?.length === 0}
              value={destinationWarehouseId}
              setValue={(val: string) => {
                setValue("destinationWarehouseId", val);
              }}
            />

            <NewinputField
              title="Capacity (Ton)"
              type="number"
              step="any"
              name="weightCapacity"
              register={register}
              error={errors.weightCapacity}
              validation={{
                required: "Capacity is required",
                min: {
                  value: 0,
                  message: "Capacity cannot be negative",
                },
              }}
              placeholder="100"
            />
            <NewinputField
              type="date"
              title="On Date"
              name="onDate"
              register={register}
              error={errors.onDate}
              validation={{ required: "On Date is required" }}
              min={new Date().toISOString().split("T")[0]}
            />
            <NewinputField
              type="time"
              title="Cut off Time"
              name="cutOffTime"
              register={register}
              error={errors.cutOffTime}
              validation={{ required: "Cut Off Time is required" }}
              placeholder="10"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestSendModal;
